<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSheet from '@/components/pos-v2/ui/PosUiSheet.vue';
  import type { TSheetHeight } from '@/components/pos-v2/ui/pos-ui-sheet.utils';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import V2CatalogCategoryNavList from './V2CatalogCategoryNavList.vue';

  /**
   * «Categorías» on phones (plan §5.7): a sheet with a search, the
   * categories with their counts, «Ordenar» and «Nueva categoría».
   */
  const props = defineProps<{ value: boolean }>();
  const emit = defineEmits<{ (e: 'input', open: boolean): void }>();

  const SEARCH_FROM = 8;

  const {
    state,
    layout,
    data,
    access,
    url,
    commands,
    jumpToCategory,
  } = useCatalogPage();
  const query = ref('');
  const height = ref<TSheetHeight>('md');

  const searchable = computed(() => data.sections.value.length > SEARCH_FROM);

  watch(
    () => props.value,
    (open) => {
      if (!open) return;
      query.value = '';
      height.value = 'md';
    }
  );

  function jump(categoryId: number) {
    emit('input', false);
    void jumpToCategory(categoryId);
  }

  function reorder() {
    emit('input', false);
    void commands.startReorder(null);
  }

  function newCategory() {
    emit('input', false);
    url.openCategory('new', layout.columns.value.asideDocked);
  }
</script>

<template>
  <PosUiSheet
    class="v2-catalog-category-sheet"
    :value="value"
    :title="$t('menuPage.nav.title')"
    :height.sync="height"
    @input="emit('input', $event)"
  >
    <div class="sheet-content">
      <h2 class="sheet-title">{{ $t('menuPage.nav.title') }}</h2>
      <PosUiInput
        v-if="searchable"
        v-model="query"
        class="sheet-search"
        icon="magnify"
        clearable
        :placeholder="$t('menuPage.nav.search')"
        :aria-label="$t('menuPage.nav.search')"
      />
      <V2CatalogCategoryNavList
        class="sheet-list"
        :query="query"
        @jump="jump"
      />
    </div>
    <div
      v-if="access.canManage && state.mode === 'browse'"
      class="sheet-actions"
    >
      <PosUiButton
        v-if="data.canReorder.value"
        color="neutral"
        variant="outline"
        icon="swap-vertical"
        @click="reorder"
        >{{ $t('menuPage.more.reorder') }}</PosUiButton
      >
      <PosUiButton
        class="action-new"
        color="neutral"
        variant="outline"
        icon="plus"
        @click="newCategory"
        >{{ $t('menuPage.more.newCategory') }}</PosUiButton
      >
    </div>
  </PosUiSheet>
</template>

<style scoped lang="scss">
  .v2-catalog-category-sheet {
    .sheet-content {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-3 tw-pb-3;
    }

    .sheet-title {
      @apply tw-m-0 tw-px-1 tw-pb-2 tw-text-base tw-font-semibold tw-text-neutral-900;
    }

    .sheet-search {
      @apply tw-mb-2;
    }

    // Margins, not flex gap (Chrome < 84).
    .sheet-actions {
      @apply tw-flex tw-flex-none tw-justify-end tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-px-4 tw-py-3;

      .action-new {
        @apply tw-ml-2;
      }
    }
  }
</style>
